import { JSONSchema7 } from 'json-schema';
import { Draft07, JsonError } from 'json-schema-library';
import { isEqual } from 'lodash-es';
import { useEffect, useState } from 'react';

import { DatasetTransformationSchema } from '@/types/schema';
import { getAPIUrl } from '@/utils/get-api-url';
import { getNestedValue } from '@/utils/get-nested-value';
import { isOpenAIMessage } from '@/utils/messages';
import { tryParseJSON } from '@/utils/try-parse-json';

import {
  DEFAULT_CHAT_TRANSFORMATIONS,
  MESSAGE_REF_URL,
  TOOL_REF_URL,
} from './constants';

// Cache for remote schemas to avoid repeated fetches
export const remoteSchemaCache: Record<string, JSONSchema7> = {};

// Function to fetch and cache remote schemas
export async function fetchRemoteSchema(url: string): Promise<JSONSchema7> {
  const resolvedUrl = `${getAPIUrl()}${url}`;
  if (remoteSchemaCache[resolvedUrl]) {
    return remoteSchemaCache[resolvedUrl];
  }

  try {
    const response = await fetch(resolvedUrl);
    const schema = await response.json();
    remoteSchemaCache[resolvedUrl] = schema;
    return schema;
  } catch (error) {
    console.error(`Failed to fetch remote schema from ${resolvedUrl}:`, error);
    throw error;
  }
}

export function getValidTransformationPaths(
  pathType: 'message' | 'tooldef',
  inputSchema?: JSONSchema7 | null
): string[] {
  function findMessageArrayPaths(
    schema: JSONSchema7 | null | undefined,
    currentPath: string = 'inputs'
  ): string[] {
    if (!schema || typeof schema !== 'object') return [];

    const paths: string[] = [];

    if (
      schema.type === 'array' &&
      schema.items &&
      !Array.isArray(schema.items) &&
      typeof schema.items !== 'boolean' &&
      schema.items?.$ref?.includes(`${pathType}.json`)
    ) {
      paths.push(currentPath);
    }

    if (schema.properties) {
      Object.entries(schema.properties).forEach(([key, value]) => {
        if (value && typeof value !== 'boolean') {
          const nestedPaths = findMessageArrayPaths(
            value,
            currentPath ? `${currentPath}.${key}` : key
          );
          paths.push(...nestedPaths);
        }
      });
    }

    return paths;
  }

  return findMessageArrayPaths(inputSchema);
}

export function getTransformationsToSet(
  transformations: DatasetTransformationSchema[] | undefined,
  inputOutput: 'inputs' | 'outputs'
) {
  const newInputTransformations = DEFAULT_CHAT_TRANSFORMATIONS.filter(
    (t) => t.path[0] === inputOutput
  );

  // Preserve existing output transformations while updating input transformations
  const existingOutputTransformations =
    transformations?.filter((t) => t.path[0] !== inputOutput) ?? [];

  return [...newInputTransformations, ...existingOutputTransformations];
}

export function schemaChanged(newSchema: unknown, oldSchema: unknown): boolean {
  return Boolean((newSchema || oldSchema) && !isEqual(newSchema, oldSchema));
}

export async function jsonStringConformsToSchema(
  value?: string | null,
  schema?: JSONSchema7 | null
): Promise<JsonError[]> {
  if (!schema) return [];
  const parsedValue = tryParseJSON(value);
  if (!parsedValue)
    return [
      {
        message: 'Invalid JSON',
        type: 'error',
        name: 'Invalid JSON',
        code: 'invalid',
        data: { pointer: '#', schema: schema, value: parsedValue },
      },
    ];

  try {
    const validator = new Draft07(schema);
    const compiled = validator.compileSchema(schema);

    // Load remote schemas if they are referenced
    const hasMessageRef = JSON.stringify(schema).includes(MESSAGE_REF_URL);
    const hasToolRef = JSON.stringify(schema).includes(TOOL_REF_URL);

    if (hasMessageRef) {
      const messageSchema = await fetchRemoteSchema(MESSAGE_REF_URL);
      validator.addRemoteSchema(MESSAGE_REF_URL, messageSchema);
    }

    if (hasToolRef) {
      const toolSchema = await fetchRemoteSchema(TOOL_REF_URL);
      validator.addRemoteSchema(TOOL_REF_URL, toolSchema);
    }
    return validator.validate(parsedValue, compiled);
  } catch (e: any) {
    console.error('Schema validation error:', e);

    return [
      {
        message: e.message ?? 'Unknown error',
        type: 'error',
        name: 'Uncaught parsing error',
        code: 'unknown',
        data: { pointer: '#', schema: schema, value: parsedValue },
      },
    ];
  }
}

export function useSchemaConformance(
  value: string | null | undefined,
  schema: JSONSchema7 | null | undefined
): JsonError[] {
  const [errors, setErrors] = useState<JsonError[]>([]);
  useEffect(() => {
    async function checkSchema() {
      if (!value || !schema) {
        setErrors([]);
        return;
      }
      const result = await jsonStringConformsToSchema(value, schema);
      setErrors(result);
    }

    checkSchema();
  }, [value, schema]);

  return errors;
}

/**
 * Extracts values from an object using an array of dot-notation paths,
 * storing them with the final key name (after the last dot)
 * @param data - The source object (can be null/undefined)
 * @param keys - Array of dot-notation paths (can be null/undefined)
 * @returns Object with final key names and extracted values
 */
export function extractFieldsWithFinalKeys(
  data: Record<string, unknown> | null | undefined,
  keys: string[] | null | undefined,
  isCSV?: boolean
): Record<string, unknown> {
  if (!data || !keys) return {};

  return keys.reduce((acc, key) => {
    const value = data[key] ?? getNestedValue(data, key);
    if (value !== undefined) {
      const finalKey = key.split('.').at(-1) ?? '';
      let parsedVal: unknown = value;
      try {
        if (typeof value === 'string' && isCSV) {
          if (value.trim() === '') {
            parsedVal = undefined;
          } else {
            parsedVal = JSON.parse(value.trim());
          }
        }
      } catch (e: unknown) {
        // Preserve original value if it's not a JSON string
      }
      acc[finalKey] = parsedVal;
    }
    return acc;
  }, {} as Record<string, unknown>);
}

/**
 * Determines suggested input and output keys from a sample JSONL row
 * @param sampleRow - The first row from the JSONL preview
 * @returns Object with suggested inputKeys and outputKeys arrays
 */
export function getSuggestedKeysFromJsonlRow(
  sampleRow: Record<string, unknown>
): { inputKeys: string[]; outputKeys: string[] } {
  const hasStructuredInputs =
    'inputs' in sampleRow &&
    sampleRow.inputs !== null &&
    typeof sampleRow.inputs === 'object' &&
    !Array.isArray(sampleRow.inputs);

  const hasStructuredOutputs =
    'outputs' in sampleRow &&
    sampleRow.outputs !== null &&
    typeof sampleRow.outputs === 'object' &&
    !Array.isArray(sampleRow.outputs);

  const inputKeys = hasStructuredInputs
    ? Object.keys(sampleRow.inputs as Record<string, unknown>).map(
        (key) => `inputs.${key}`
      )
    : [Object.keys(sampleRow)[0]].filter((key) => key != null);

  const outputKeys = hasStructuredOutputs
    ? Object.keys(sampleRow.outputs as Record<string, unknown>).map(
        (key) => `outputs.${key}`
      )
    : ([
        Object.keys(sampleRow).find((key) => {
          // If inputs are structured, exclude the 'inputs' key itself
          // Otherwise, exclude the first key that was selected as input
          return hasStructuredInputs
            ? key !== 'inputs'
            : !inputKeys.includes(key);
        }),
      ].filter((key) => key != null) as string[]);

  return { inputKeys, outputKeys };
}

/**
 * Applies key mappings to extracted fields, renaming keys according to the mappings
 * @param extractedFields - Fields extracted using extractFieldsWithFinalKeys
 * @param keys - Original key names array
 * @param mappings - Key mappings (original -> renamed)
 * @returns Object with renamed keys
 */
export function applyKeyMappings(
  extractedFields: Record<string, unknown>,
  keys: string[] | null,
  mappings: Record<string, string> = {}
): Record<string, unknown> {
  if (!keys) return extractedFields;

  const result: Record<string, unknown> = {};

  keys.forEach((originalKey) => {
    const finalKey = originalKey.split('.').at(-1) ?? '';
    const mappedName = mappings[originalKey] || finalKey;
    const value = extractedFields[finalKey];
    if (value !== undefined) {
      result[mappedName] = value;
    }
  });

  return result;
}

/**
 * Infers JSON Schema from sample data values
 * @param value - The value to analyze
 * @returns JSON Schema object describing the value type
 */
function inferJsonSchemaFromValue(
  value: unknown,
  isCSV: boolean
): Record<string, unknown> {
  if (value === null) {
    return { type: 'null' };
  }

  if (typeof value === 'string') {
    if (isCSV && ['TRUE', 'FALSE'].includes(value)) {
      return { type: 'boolean' };
    }
    return { type: 'string' };
  }

  if (typeof value === 'number') {
    return { type: 'number' };
  }

  if (typeof value === 'boolean') {
    return { type: 'boolean' };
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return { type: 'array', items: {} };
    }

    // Check if this is an array of OpenAI messages
    if (value.every((item) => isOpenAIMessage(item))) {
      return {
        type: 'array',
        items: {
          $ref: MESSAGE_REF_URL,
        },
      };
    }

    // Infer schema from first item, could be enhanced to analyze all items
    const itemSchema = inferJsonSchemaFromValue(value[0], isCSV);
    return { type: 'array', items: itemSchema };
  }

  if (typeof value === 'object') {
    // Check if this is a single OpenAI message
    if (isOpenAIMessage(value)) {
      return {
        $ref: MESSAGE_REF_URL,
      };
    }

    const properties: Record<string, unknown> = {};
    const required: string[] = [];

    for (const [key, val] of Object.entries(value)) {
      properties[key] = inferJsonSchemaFromValue(val, isCSV);
      if (val !== null && val !== undefined) {
        required.push(key);
      }
    }

    return {
      type: 'object',
      properties,
      ...(required.length > 0 ? { required } : {}),
    };
  }

  // Fallback for unknown types
  return {};
}

/**
 * Infers input and output schemas from file preview data
 * @param filePreview - Array of sample rows from CSV/JSONL
 * @param inputKeys - Array of input field keys
 * @param outputKeys - Array of output field keys
 * @param isCSV - Whether the data is from CSV format
 * @param inputKeyMappings - Mappings for input key names (original -> renamed)
 * @param outputKeyMappings - Mappings for output key names (original -> renamed)
 * @returns Object with inferred input and output schemas
 */
export function inferSchemasFromFilePreview(
  filePreview: Array<Record<string, unknown>>,
  inputKeys: string[] | null,
  outputKeys: string[] | null,
  isCSV?: boolean,
  inputKeyMappings?: Record<string, string>,
  outputKeyMappings?: Record<string, string>
): {
  inputSchema: Record<string, unknown> | null;
  outputSchema: Record<string, unknown> | null;
} {
  if (!filePreview?.length || (!inputKeys?.length && !outputKeys?.length)) {
    return { inputSchema: null, outputSchema: null };
  }

  let inputSchema: Record<string, unknown> | null = null;
  let outputSchema: Record<string, unknown> | null = null;

  if (inputKeys?.length) {
    // Extract input data from sample rows and apply key mappings
    const inputSamples = filePreview
      .map((row) => {
        const extracted = extractFieldsWithFinalKeys(row, inputKeys, isCSV);
        return applyKeyMappings(extracted, inputKeys, inputKeyMappings);
      })
      .filter((sample) => Object.keys(sample).length > 0);

    if (inputSamples.length > 0) {
      // Merge properties from all samples to get comprehensive schema
      const allInputProps: Record<string, unknown> = {};
      const inputRequired = new Set<string>();

      inputSamples.forEach((sample) => {
        Object.entries(sample).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            allInputProps[key] = inferJsonSchemaFromValue(
              value,
              isCSV ?? false
            );
            inputRequired.add(key);
          }
        });
      });

      inputSchema = {
        type: 'object',
        properties: allInputProps,
        ...(inputRequired.size > 0
          ? { required: Array.from(inputRequired) }
          : {}),
      };
    }
  }

  if (outputKeys?.length) {
    // Extract output data from sample rows and apply key mappings
    const outputSamples = filePreview
      .map((row) => {
        const extracted = extractFieldsWithFinalKeys(row, outputKeys, isCSV);
        return applyKeyMappings(extracted, outputKeys, outputKeyMappings);
      })
      .filter((sample) => Object.keys(sample).length > 0);

    if (outputSamples.length > 0) {
      // Merge properties from all samples to get comprehensive schema
      const allOutputProps: Record<string, unknown> = {};
      const outputRequired = new Set<string>();

      outputSamples.forEach((sample) => {
        Object.entries(sample).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            allOutputProps[key] = inferJsonSchemaFromValue(
              value,
              isCSV ?? false
            );
            outputRequired.add(key);
          }
        });
      });

      outputSchema = {
        type: 'object',
        properties: allOutputProps,
        ...(outputRequired.size > 0
          ? { required: Array.from(outputRequired) }
          : {}),
      };
    }
  }

  return { inputSchema, outputSchema };
}
